import React from "react";
import Meta from "../Meta";
import PageHeader from "../components/PageHeader";
import EmptyState from "../components/EmptyState";
import EventsPreview from "../sections/EventsPreview";
import Loader from "../loader";
import { applyLang } from "../../../i18n";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import Link from "next/link";
import Icon from "../components/Icon";
import { resolveImage } from "../utils";
import { useEvents } from "../../../services/EventService";

const styles = `
.t26-events-page {
  padding: 0 clamp(16px, 4vw, 32px) clamp(40px, 6vw, 80px);
  max-width: var(--container);
  margin-inline: auto;
}
.t26-events-page__grid {
  display: grid;
  /* max 520px par card pour ne pas s'étirer démesurément quand il n'y a
     qu'un seul événement, mais permet de remplir la grille proprement
     à 2, 3, 4… colonnes selon la largeur disponible. */
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 320px), 520px));
  justify-content: center;
  gap: clamp(16px, 2.5vw, 28px);
}
.t26-event-row {
  display: flex; gap: 14px;
  padding: 14px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  transition: transform .2s, box-shadow .2s, border-color .2s;
  text-decoration: none;
  color: inherit;
}
.t26-event-row:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
  border-color: color-mix(in oklab, var(--primary) 35%, var(--line));
}
.t26-event-row__img {
  flex-shrink: 0;
  width: 120px;
  aspect-ratio: 4 / 5;
  border-radius: var(--r-md);
  overflow: hidden;
  background: color-mix(in oklab, var(--primary) 12%, var(--bg-2));
}
.t26-event-row__img img { width: 100%; height: 100%; object-fit: cover; }
.t26-event-row__body {
  display: flex; flex-direction: column; gap: 6px;
  min-width: 0;
}
.t26-event-row__date {
  font-size: 11px; font-weight: 700;
  letter-spacing: .14em; text-transform: uppercase;
  color: var(--secondary);
}
.t26-event-row__title {
  font-family: var(--font-display);
  font-size: 17px; font-weight: 700;
  line-height: 1.2;
  color: var(--ink);
  margin: 0;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.t26-event-row__meta {
  font-size: 12px;
  color: var(--ink-3);
  display: inline-flex; align-items: center; gap: 6px;
}
`;

function toMs(v) {
    if (!v) return null;
    const ms = typeof v === "number" ? v : Date.parse(v);
    return isNaN(ms) ? null : ms;
}

function startOfToday() {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime();
}

function formatPeriod(startMs, endMs, locale) {
    if (!startMs) return "";
    const start = new Date(startMs);
    const opts = { weekday: "long", day: "numeric", month: "long", year: "numeric" };
    const timeOpts = { hour: "2-digit", minute: "2-digit" };
    const hasTime = start.getHours() !== 0 || start.getMinutes() !== 0;
    const loc = locale || "fr";
    const timeStr = hasTime ? start.toLocaleTimeString(loc, timeOpts) : null;

    if (!endMs || Math.abs(endMs - startMs) < 24 * 3600 * 1000) {
        const dateStr = start.toLocaleDateString(loc, opts);
        return timeStr ? `${dateStr} · ${timeStr}` : dateStr;
    }
    const end = new Date(endMs);
    const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
    if (sameMonth) {
        const monthYear = end.toLocaleDateString(loc, { month: "long", year: "numeric" });
        return `${start.getDate()} → ${end.getDate()} ${monthYear}`;
    }
    const startStr = start.toLocaleDateString(loc, { day: "numeric", month: "short" });
    const endStr = end.toLocaleDateString(loc, { day: "numeric", month: "long", year: "numeric" });
    return `${startStr} → ${endStr}`;
}

export default function Events({ context, loading }) {
    const { org, lang, langValue } = context;
    // Événements fetchés CÔTÉ CLIENT via /v1/events (modèle galeries/actualités).
    const { events: allEvents, loading: eventsLoading } = useEvents(context);

    const todayStart = startOfToday();

    // Filtre identique à EventsPreview : active, non masqué, et fin >= aujourd'hui.
    const events = (allEvents || [])
        .filter(e => e && e.active !== false && !e.hidden)
        .map(e => {
            const startMs = toMs(e.dateEvent || e.date_start || e.date);
            const endMs = toMs(e.dateFin || e.date_end) || startMs;
            return { e, startMs, endMs };
        })
        .filter(({ startMs, endMs }) => {
            if (!startMs) return false;
            return (endMs || startMs) >= todayStart;
        })
        .sort((a, b) => (a.startMs || 0) - (b.startMs || 0));

    // Rester sur le Loader tant que le fetch client n'a pas répondu (sinon on
    // flasherait l'EmptyState "aucun événement" avant l'arrivée des données).
    if (loading || eventsLoading) return <Loader />;

    return (
        <>
            <Meta context={context} title={t`Événements`} description={t`Tous les événements à venir`} />
            <style dangerouslySetInnerHTML={{ __html: styles }} />

            <PageHeader
                title={t`Événements`}
                titleEm={t`à venir`}
                subtitle={t`Découvrez tous nos prochains rendez-vous`}
                crumbs={[
                    { label: t`Accueil`, url: org.BASE_WEB_URL },
                    { label: t`Événements` },
                ]}
            />

            <div className="t26-events-page">
                {events.length === 0 ? (
                    <EmptyState
                        icon="IconCalendarOff"
                        title={t`Aucun événement programmé`}
                        subtitle={t`Revenez bientôt pour découvrir nos prochains rendez-vous.`}
                    />
                ) : (
                    <div className="t26-events-page__grid">
                        {events.map(({ e: ev, startMs, endMs }) => {
                            const localized = applyLang(ev, lang);
                            const dateLabel = ev.isMultiSession ? null : formatPeriod(startMs, endMs, langValue);
                            const url = `${org.BASE_WEB_URL}/evenement/${ev.urlSlug || ev._id || ev.id}`;

                            return (
                                <Link key={ev._id || ev.id} href={url} legacyBehavior>
                                    <a className="t26-event-row">
                                        <span style={{display:"block"}} className="t26-event-row__img">
                                            {ev.image ? <img src={resolveImage(ev.image)} alt={localized.name || ""} loading="lazy" /> : null}
                                        </span>
                                        <span style={{display:"block"}} className="t26-event-row__body">
                                            {dateLabel && <span className="t26-event-row__date">{dateLabel}</span>}
                                            <h3 className="t26-event-row__title">{localized.name}</h3>
                                            {localized.address?.address && (
                                                <span className="t26-event-row__meta">
                                                    <Icon name="mapPin" size={12} />
                                                    {localized.address.address}
                                                </span>
                                            )}
                                            {ev.isMultiSession && (
                                                <span className="t26-event-row__meta" style={{ color: "var(--secondary)", fontWeight: 600 }}>
                                                    <Trans>Plusieurs sessions disponibles</Trans>
                                                </span>
                                            )}
                                        </span>
                                    </a>
                                </Link>
                            );
                        })}
                    </div>
                )}
            </div>
        </>
    );
}
